import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useEntitlement } from "@/hooks/useEntitlement";
import { useAuth } from "@/lib/auth";
import { supabase } from "@/integrations/supabase/client";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { setOrgFeatureFlag } from "@/lib/modules.functions";

export const Route = createFileRoute("/_authenticated/modules")({
  component: ModulesPage,
  head: () => ({ meta: [{ title: "Modules — MissionReady" }] }),
});

/** Modules only available on an Enterprise plan. */
const ENTERPRISE_ONLY = new Set(["white_label", "api_access"]);

const MODS = [
  { key: "dispatch", label: "Dispatch", desc: "Real-time dispatch and broadcast." },
  { key: "gps_tracking", label: "GPS Tracking", desc: "Live officer GPS map and history." },
  { key: "ai_reporting", label: "AI Reporting", desc: "AI-assisted incident summaries." },
  { key: "fleet", label: "Fleet & Vehicles", desc: "Vehicle inspections and tracking." },
  { key: "scheduling", label: "Scheduling", desc: "Shift scheduling and open shifts." },
  { key: "advanced_analytics", label: "Advanced Analytics", desc: "Heatmaps and deep dashboards." },
  { key: "white_label", label: "White Labeling", desc: "Custom logo, colors, domain." },
  { key: "api_access", label: "API Access", desc: "Programmatic API tokens." },
] as const;

function ModulesPage() {
  const { currentOrg, currentRole } = useAuth();
  const ent = useEntitlement();
  const canEdit = currentRole === "org_owner";
  const hasEnterprise = ent.exempt || (ent.plan_key?.startsWith("enterprise") ?? false);
  const [flags, setFlags] = useState<Record<string, boolean>>({});
  const setFlag = useServerFn(setOrgFeatureFlag);

  useEffect(() => {
    if (!currentOrg) return;
    (async () => {
      const { data } = await supabase
        .from("org_feature_flags")
        .select("*")
        .eq("organization_id", currentOrg.id)
        .maybeSingle();
      if (data) {
        setFlags(data as never);
      } else {
        // No row yet — seed defaults so toggles persist
        const { data: inserted } = await supabase
          .from("org_feature_flags")
          .insert({ organization_id: currentOrg.id })
          .select("*")
          .maybeSingle();
        if (inserted) setFlags(inserted as never);
      }
    })();
  }, [currentOrg?.id]);

  const toggle = async (key: string, value: boolean) => {
    if (!currentOrg || !canEdit) return;
    if (value && ENTERPRISE_ONLY.has(key) && !hasEnterprise) {
      toast.error("This module is available on the Enterprise plan.");
      return;
    }
    setFlags((f) => ({ ...f, [key]: value }));
    try {
      await setFlag({ data: { orgId: currentOrg.id, key: key as any, value } });
    } catch (e: any) { toast.error(e?.message ?? "Failed"); }
  };

  return (
    <div className="px-4 py-6 md:px-8 md:py-8">
      <h1 className="font-display text-2xl font-bold md:text-3xl">Modules</h1>
      <p className="text-sm text-muted-foreground">Enable features as your operation grows.</p>

      <div className="mt-6 grid gap-3 sm:grid-cols-2">
        {MODS.map((m) => (
          <div key={m.key} className="flex items-start justify-between rounded-xl border border-border bg-card p-5">
            <div>
              <h3 className="font-display text-lg font-semibold">{m.label}</h3>
              <p className="mt-1 text-sm text-muted-foreground">{m.desc}</p>
              {ENTERPRISE_ONLY.has(m.key) && !hasEnterprise && (
                <p className="mt-2 text-xs font-medium text-muted-foreground">Enterprise plan required</p>
              )}
            </div>
            <Switch
              checked={!!flags[m.key]}
              disabled={!canEdit || (ENTERPRISE_ONLY.has(m.key) && !hasEnterprise)}
              onCheckedChange={(v) => toggle(m.key, v)}
            />
          </div>
        ))}
      </div>
      {!canEdit && (
        <p className="mt-4 text-xs text-muted-foreground">Only the workspace owner can change modules.</p>
      )}
    </div>
  );
}
